import React, { useEffect, useState } from 'react';
import useSettings from '../../../hooks/useSettings';
import {
	TemplateBoolean,
	TemplateCheckbox,
	TemplateDropdown,
	TemplateField,
	TemplateImage,
} from '../../../components/Attributes/Attributes_old';
import useAsync from '../../../hooks/useAsync';
import { getModuleFields, getModuleFieldsBeta } from '../../../api/modules';
import Button from '../../../components/Button';
import { QrCodeIcon } from '@heroicons/react/24/outline';
import QRCode from 'react-qr-code';
import useLocal from '../../../hooks/useLocal';

const ASSETS_MODULE_ID = process.env.REACT_APP_MODULE_ID_ASSETS;

const QRCodeSetting = () => {
	const [copyText, setCopyText] = useState('Copy');
	const { get: getSetting, set: setSetting } = useSettings();
	const { get: getLocalData } = useLocal();

	const { value: moduleFields } = useAsync(getModuleFields, {
		defaultPayload: { id: ASSETS_MODULE_ID },
	});

	const areAssetTagsEnabled = getSetting('assetTags');
	const assetTagReportButtonEnabled = getSetting('assetTagReportButton');
	const assetTagPinConfiguration = getSetting('assetTagPinConfiguration');

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setCopyText('Copy');
		}, 2000); // Change text after 2 seconds

		return () => clearTimeout(timeoutId);
	}, [copyText]);

	const NODE_ENV = process.env.REACT_APP_ENVIRONMENT;

	const QRURL = `${
		NODE_ENV === 'development'
			? 'http://localhost:8080'
			: NODE_ENV === 'stage'
			? 'https://stage.cloud.scipods.com'
			: 'https://cloud.scipods.com'
	}/c/${getLocalData('user')?.organization?.id || '0'}`;

	const PIN_CONFIGURATION_OPTIONS = [
		{ label: '4 Pin (Default)', value: 'default' },
		...(moduleFields?.length
			? moduleFields
					?.filter(
						(attr) =>
							[
								'manufacturer',
								'model',
								'serialNumber',
								'location',
								'category',
								'tags',
							].indexOf(attr?.key) < 0
					)
					.filter((attr) => attr.type === 'text')
					.map((attr) => ({ value: attr.id, label: attr.label }))
			: []),
	];
	return (
		<>
			<div>
				<h2 className="text-base font-semibold leading-6 text-gray-900">
					Asset Tags
				</h2>
				<p className="text-xs my-4 text-gray-600">
					Scipods asset tags work a bit differently than standard asset tags.
					Our asset tags include any details that you want to include share with
					anyone in your lab.
				</p>

				<div>
					<TemplateBoolean
						name={areAssetTagsEnabled ? 'Enabled' : 'Disabled'}
						value={areAssetTagsEnabled}
						onChange={(index, value) =>
							setSetting('company', 'assetTags', value ? [] : null, {
								showNotification: false,
							})
						}
					/>
				</div>
				{areAssetTagsEnabled ? (
					<>
						<div className="mt-10 mb-4 text-sm font-semibold">
							Public QR Code
						</div>
						<QRCode id="QRCode" title="Custom Title" value={QRURL} />

						<div className="flex mt-4">
							<TemplateField
								className="flex-1"
								value={QRURL}
								onChange={() => {}}
							/>
							<Button
								type="secondary"
								onClick={() => {
									navigator.clipboard.writeText(QRURL);
									setCopyText('Copied!');
								}}
							>
								{copyText}
							</Button>
							<Button
								Icon={QrCodeIcon}
								onClick={() => {
									const svg = document.getElementById('QRCode');
									const svgData = new XMLSerializer().serializeToString(svg);
									const canvas = document.createElement('canvas');
									const ctx = canvas.getContext('2d');
									const img = new Image();
									img.onload = () => {
										canvas.width = img.width;
										canvas.height = img.height;
										ctx.drawImage(img, 0, 0);
										const pngFile = canvas.toDataURL('image/png');
										const downloadLink = document.createElement('a');
										downloadLink.download = 'ScipodsQRCode';
										downloadLink.href = `${pngFile}`;
										downloadLink.click();
									};
									img.src = `data:image/svg+xml;base64,${btoa(svgData)}`;
								}}
							>
								Download QR
							</Button>
						</div>
						<div className="text-xxs text-400 mt-4">
							In order to view the public information, you must be signed out.
							If you are signed in, you will view the regular asset page
						</div>

						<div className="mt-10 mb-4 text-sm font-semibold">
							Public Attributes
						</div>

						<fieldset>
							<div className="divide-y divide-gray-200 border-gray-200">
								{[
									{ id: 'manufacturer', name: 'Manufacturer', default: true },
									{ id: 'model', name: 'Model', default: true },
									{ id: 'serial', name: 'Serial Number', default: true },
									{ id: 'status', name: 'Status' },
									{ id: 'location', name: 'Location' },
									{ id: 'category', name: 'Category' },
									{ id: 'nextPM', name: 'Next Scheduled PM' },
									...(moduleFields?.length
										? moduleFields
												?.filter(
													(attr) =>
														[
															'manufacturer',
															'model',
															'serialNumber',
															'location',
															'category',
															'tags',
														].indexOf(attr?.key) < 0
												)
												.map((attr) => ({ id: attr.id, name: attr.label }))
										: []),
								].map((attribute, attributeIdx) => (
									<div
										key={attributeIdx}
										className="relative flex items-center py-4"
									>
										<div className="min-w-0 flex-1 text-sm leading-6">
											<label
												htmlFor={`person-${attribute.id}`}
												className="select-none text-xs text-gray-900"
											>
												{attribute.name}{' '}
												{attribute?.default && (
													<span className="text-gray-400 text-xxs">
														(Default)
													</span>
												)}
											</label>
										</div>
										<div className="ml-3 flex h-6 items-center">
											<TemplateBoolean
												disabled={attribute?.default}
												value={
													attribute?.default ||
													areAssetTagsEnabled?.includes(attribute?.id)
												}
												onChange={(index, value) => {
													if (value) {
														setSetting(
															'company',
															'assetTags',
															[
																...(areAssetTagsEnabled?.length
																	? areAssetTagsEnabled
																	: []),
																attribute.id,
															],
															{
																showNotification: false,
															}
														);
													} else {
														setSetting(
															'company',
															'assetTags',
															[
																...(areAssetTagsEnabled?.length
																	? areAssetTagsEnabled?.filter(
																			(enabled) => enabled !== attribute.id
																	  )
																	: []),
															],
															{
																showNotification: false,
															}
														);
													}
												}}
											/>
										</div>
									</div>
								))}
							</div>
						</fieldset>
						<div className="mt-10 mb-4 text-sm font-semibold">Additional</div>
						<fieldset>
							<div className="divide-y divide-gray-200 border-gray-200">
								{[{ id: 'report-issue', name: 'Allow Report Issue' }].map(
									(attribute, attributeIdx) => (
										<div
											key={attributeIdx}
											className="relative flex flex-col py-4"
										>
											<div className="flex flex-1">
												<div className="min-w-0 flex-1 text-sm leading-6">
													<label
														htmlFor={`person-${attribute.id}`}
														className="select-none text-xs text-gray-900"
													>
														{attribute.name}
													</label>
												</div>
												<div className="ml-3 flex h-6 items-center">
													<TemplateBoolean
														disabled={attribute?.default}
														value={
															attribute?.default ||
															assetTagReportButtonEnabled?.enabled
														}
														onChange={(index, value) => {
															if (value) {
																setSetting(
																	'company',
																	'assetTagReportButton',
																	{
																		enabled: true,
																	},
																	{
																		showNotification: false,
																	}
																);
															} else {
																setSetting(
																	'company',
																	'assetTagReportButton',
																	null,
																	{
																		showNotification: false,
																	}
																);
															}
														}}
													/>
												</div>
											</div>
										</div>
									)
								)}
								<div className="relative flex flex-col py-4">
									<div className="flex flex-1">
										<div className="min-w-0 flex-1 text-sm leading-6">
											<label className="select-none text-xs text-gray-900">
												Pin Configuration
											</label>
										</div>
										<div className="ml-3 flex h-6 items-center">
											<TemplateDropdown
												className={'w-72'}
												options={PIN_CONFIGURATION_OPTIONS}
												value={assetTagPinConfiguration?.value || 'default'}
												onChange={(index, value, raw) => {
													if (value) {
														setSetting(
															'company',
															'assetTagPinConfiguration',
															raw,
															{
																showNotification: false,
															}
														);
													} else {
														setSetting(
															'company',
															'assetTagPinConfiguration',
															null,
															{
																showNotification: false,
															}
														);
													}
												}}
											/>
										</div>
									</div>
								</div>
							</div>
						</fieldset>
						{assetTagReportButtonEnabled?.enabled ? (
							<div className="px-10 text-xs">
								<div className="text-xs">Configuration</div>
								<div className="grid gap-y-3 mt-4 grid-cols-2">
									<TemplateBoolean
										description="Reported by"
										value={assetTagReportButtonEnabled?.reportedBy}
										onChange={(index, reportedBy) =>
											setSetting(
												'company',
												'assetTagReportButton',
												{
													...assetTagReportButtonEnabled,
													reportedBy,
												},
												{ showNotification: false }
											)
										}
									/>
									<TemplateBoolean
										description="Overview (Required: default ticket title)"
										value={true}
										onChange={() => {}}
									/>
									<TemplateBoolean
										description="Details"
										value={assetTagReportButtonEnabled?.details}
										onChange={(index, details) =>
											setSetting(
												'company',
												'assetTagReportButton',
												{
													...assetTagReportButtonEnabled,
													details,
												},
												{ showNotification: false }
											)
										}
									/>
									<TemplateBoolean
										description="Photos"
										value={assetTagReportButtonEnabled?.photos}
										onChange={(index, photos) =>
											setSetting(
												'company',
												'assetTagReportButton',
												{
													...assetTagReportButtonEnabled,
													photos,
												},
												{ showNotification: false }
											)
										}
									/>
									<TemplateBoolean
										description="Submitted from"
										value={assetTagReportButtonEnabled?.submittedFrom}
										onChange={(index, submittedFrom) =>
											setSetting(
												'company',
												'assetTagReportButton',
												{
													...assetTagReportButtonEnabled,
													submittedFrom,
												},
												{ showNotification: false }
											)
										}
									/>
									{assetTagReportButtonEnabled?.submittedFrom && (
										<TemplateField
											className="mt-2"
											placeholder={`Enter value that will appear on ticket.  Default: "QR code" `}
											value={assetTagReportButtonEnabled?.submittedFromValue}
											onChange={(index, submittedFromValue) =>
												setSetting(
													'company',
													'assetTagReportButton',
													{
														...assetTagReportButtonEnabled,
														submittedFromValue,
													},
													{ showNotification: false }
												)
											}
										/>
									)}
								</div>
							</div>
						) : null}
					</>
				) : null}
			</div>
		</>
	);
};

export default QRCodeSetting;
